import React, { Fragment } from 'react';
import * as R from 'ramda';
import { getData } from 'country-list';
import { Form, Button, Icon, Select } from 'antd';
import { object, func, string, bool, array } from 'prop-types';

import { getMaxFieldLengthRule } from '../../utils';
import { CancelButton } from '../company-form/styled-components';
import { URL, EMAIL, INTERNATIONAL_PHONE } from '../../utils/regexp';
import {
  Title,
  FormItem,
  InputField,
  ButtonSave,
  StyledSelect,
  ButtonWrapper,
  SelectDropdown,
  RoundButtonCancel,
} from './styled-components';

const { Option } = Select;

const ContactForm = ({
  form: { getFieldDecorator, getFieldValue, setFieldsValue },
  onSubmitForm,
  contact: {
    positionAtCompany,
    fullName,
    linkedIn,
    country,
    company,
    emails,
    phones,
    skype,
  },
  title,
  isSubForm,
  isLoading,
  closeModal,
  addEmailField,
  addPhoneField,
  companiesList,
  handleHideForm,
  removePhoneField,
  removeEmailField,
  isFetchingCompanies,
  handleCompaniesSearch,
}) => {
  const companyId = getFieldValue('companyId');

  getFieldDecorator('emailKeys', {
    initialValue: emails && !R.isEmpty(emails) ?
      Array.from(new Array(emails.length), (x, i) => i) :
      [0],
  });
  const emailKeys = getFieldValue('emailKeys');
  const formEmailItems = emailKeys.map((k, index) => (
    <FormItem
      required={false}
      key={k}
      colon={false}
      label={index === 0 ? 'E-mails' : null}
    >
      {getFieldDecorator(`emails[${k}]`, {
        validateTrigger: ['onChange', 'onBlur'],
        rules: [
          {
            pattern: EMAIL,
            whitespace: true,
            message: 'Please input valid E-mail!',
          },
          {
            required: false,
          },
        ],
        initialValue: emails && emails[k],
      })(<InputField
        placeholder="Enter E-mail..."
        style={{
          width: emailKeys.length > 1 ? 'calc(100% - 32px)' : '100%',
          marginRight: 8,
          transition: 'none',
          height: '40px',
        }}
      />)}
      {emailKeys.length > 1 ? (
        <Icon
          className="dynamic-delete-button"
          type="minus-circle-o"
          onClick={() => removeEmailField(k)}
        />
      ) : null }
    </FormItem>
  ));

  getFieldDecorator('phoneKeys', {
    initialValue: phones && !R.isEmpty(phones) ?
      Array.from(new Array(phones.length), (x, i) => i) :
      [0],
  });
  const phoneKeys = getFieldValue('phoneKeys');
  const formPhoneItems = phoneKeys.map((k, index) => (
    <FormItem
      required={false}
      key={k}
      colon={false}
      label={index === 0 ? 'Phone numbers' : null}
    >
      {getFieldDecorator(`phones[${k}]`, {
        validateTrigger: ['onChange', 'onBlur'],
        rules: [
          {
            whitespace: true,
            pattern: INTERNATIONAL_PHONE,
            message: 'Please input valid phone number!',
            transform: (value = '') => {
              const includesPlusSign = value.search(new RegExp(/\+/, 'g')) === 0;
              if (!includesPlusSign && value.length > 0) {
                const newValue = `+${value}`;
                setFieldsValue({ [`phones[${k}]`]: newValue });
                return newValue;
              }
              return value;
            },
          },
          {
            required: false,
          },
        ],
        initialValue: phones && phones[k],
      })(<InputField
        placeholder="Enter Phone Number..."
        style={{
          width: phoneKeys.length > 1 ? 'calc(100% - 32px)' : '100%',
          marginRight: 8,
          transition: 'none',
          height: '40px',
        }}
      />)}
      {phoneKeys.length > 1 ? (
        <Icon
          className="dynamic-delete-button"
          type="minus-circle-o"
          onClick={() => removePhoneField(k)}
        />
      ) : null }
    </FormItem>
  ));

  return (
    <Fragment>
      {!isSubForm && <Title>{title}</Title>}
      <Form onSubmit={onSubmitForm}>
        <FormItem
          colon={false}
          label="Full name"
        >
          {getFieldDecorator('fullName', {
            rules: [
              {
                required: true,
                message: 'Please input Full name!',
              },
              getMaxFieldLengthRule(70),
            ],
            initialValue: fullName,
          })(<InputField
            size="large"
            name="fullName"
            placeholder="Enter Full name..."
          />)}
        </FormItem>

        {formEmailItems}

        <FormItem>
          <Button type="dashed" onClick={addEmailField} style={{ width: '100%' }}>
            <Icon type="plus" /> Add another E-mail
          </Button>
        </FormItem>

        {formPhoneItems}

        <FormItem>
          <Button type="dashed" onClick={addPhoneField} style={{ width: '100%' }}>
            <Icon type="plus" /> Add another Phone Number
          </Button>
        </FormItem>

        <FormItem
          colon={false}
          label="Country"
        >
          <div onMouseDown={(e) => {
            e.preventDefault();
            return false;
          }}
          >
            {getFieldDecorator('country', {
              ...(country && { initialValue: country }),
            })(<StyledSelect
              showSearch
              allowClear
              placeholder="Select Country..."
              defaultActiveFirstOption={false}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0
              }
              getPopupContainer={triggerNode => triggerNode.parentNode}
            >
              {getData().map(item => (
                <Option key={item.code} value={item.code}>
                  {item.name}
                </Option>
              ))}
            </StyledSelect>)}
          </div>
        </FormItem>

        <FormItem
          colon={false}
          label="Skype"
        >
          {getFieldDecorator('skype', {
            rules: [
              {
                whitespace: true,
                message: 'Please input valid Skype!',
              },
            ],
            initialValue: skype,
          })(<InputField placeholder="Enter Skype..." size="large" name="skype" />)}
        </FormItem>

        <FormItem
          colon={false}
          label="LinkedIn"
        >
          {getFieldDecorator('linkedIn', {
            rules: [
              {
                pattern: URL,
                message: 'Please input valid LinkedIn URL!',
              },
            ],
            initialValue: linkedIn,
          })(<InputField placeholder="Enter LinkedIn URL..." size="large" name="linkedIn" />)}
        </FormItem>

        <FormItem
          colon={false}
          label="Company"
        >
          <div onMouseDown={(e) => {
            e.preventDefault();
            return false;
          }}
          >
            {getFieldDecorator('companyId', {
              ...(company && { initialValue: company._id }),
            })(<StyledSelect
              showSearch
              allowClear
              filterOption={false}
              loading={isFetchingCompanies}
              onSearch={handleCompaniesSearch}
              defaultActiveFirstOption={false}
              placeholder="Search Company by name..."
              getPopupContainer={triggerNode => triggerNode.parentNode}
              dropdownRender={(menu) => {
                if (!isFetchingCompanies && menu.props.inputValue.length > 1) {
                  return (
                    <SelectDropdown>
                      {menu}
                    </SelectDropdown>
                  );
                }
                return (<SelectDropdown />);
              }}
            >
              { companiesList.map(item => (
                <Option key={item._id} value={item._id}>
                  {item.name}
                </Option>
              ))}
            </StyledSelect>)}
          </div>
        </FormItem>

        { (!!companyId || (company && company._id)) && (
          <FormItem
            colon={false}
            label="Position at Company"
          >
            {getFieldDecorator('positionAtCompany', {
              initialValue: positionAtCompany,
            })(<InputField
              size="large"
              name="positionAtCompany"
              placeholder="Enter Position..."
            />)}
          </FormItem>
        )}

        <ButtonWrapper {...(isSubForm && { className: 'centered' })}>
          {isSubForm
            ? <RoundButtonCancel
              onClick={handleHideForm}
              disabled={isLoading}
            >
              Cancel
            </RoundButtonCancel>
            : <CancelButton
              onClick={closeModal}
              disabled={isLoading}
            >
              Cancel
            </CancelButton>
          }
          <ButtonSave
            key="submit"
            type="primary"
            htmlType="submit"
            loading={isLoading}
            {...(isSubForm && { className: 'centered' })}
          >
            Save
          </ButtonSave>
        </ButtonWrapper>
      </Form>
    </Fragment>
  );
};

ContactForm.propTypes = {
  removePhoneField: func.isRequired,
  removeEmailField: func.isRequired,
  handleHideForm: func,
  addPhoneField: func.isRequired,
  addEmailField: func.isRequired,
  onSubmitForm: func.isRequired,
  isSubForm: bool,
  title: string.isRequired,
  form: object.isRequired,
  contact: object,
  isLoading: bool,
  companiesList: array.isRequired,
  isFetchingCompanies: bool.isRequired,
  handleCompaniesSearch: func.isRequired,
  closeModal: func.isRequired,
};

ContactForm.defaultProps = {
  isSubForm: false,
  isLoading: false,
  contact: {},
  handleHideForm: () => {},
};

export default ContactForm;



// WEBPACK FOOTER //
// ./src/components/contact-form/index.js